import { useState } from 'react'
import { useGameStore } from '../store/gameStore'
import { useI18n } from '../i18n/I18nProvider'
import Button from './Button'
import { Sprout, Flower2, Sparkles, Trees, Flame, Star, Crown, Trophy } from 'lucide-react'

const seedIcons = {
  default: Sprout,
  small_blue: Sprout,
  pink_flower: Flower2,
  rainbow: Sparkles,
  tall_purple: Trees,
  fire_red: Flame,
  rare: Star,
  legendary: Crown,
  golden_giant: Trophy,
}

function SeedSelector({ onPlant }) {
  const { t } = useI18n()
  const config = useGameStore((state) => state.config)
  const seedInventory = useGameStore((state) => state.seedInventory)
  const [selectedSeedType, setSelectedSeedType] = useState(null)

  if (!config) return null

  // Obtenir toutes les graines disponibles
  const availableSeeds = Object.entries(seedInventory).map(([plantType, quantity]) => {
    const plantConfig = config.plants[plantType]
    return {
      type: plantType,
      quantity,
      config: plantConfig,
    }
  })

  const totalSeeds = Object.values(seedInventory).reduce((sum, qty) => sum + qty, 0)

  if (totalSeeds === 0) {
    return (
      <div className="text-center text-gray-500">
        <Sprout size={48} className="mx-auto mb-4 text-gray-400" />
        <p>{t('seedSelector.noSeeds')}</p>
        <p className="text-sm mt-2">{t('seedSelector.growPlantToGetSeeds')}</p>
      </div>
    )
  }

  const handlePlant = () => {
    if (selectedSeedType && onPlant) {
      onPlant(selectedSeedType)
      setSelectedSeedType(null)
    }
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        {availableSeeds.map((seed) => {
          const Icon = seedIcons[seed.type] || Sprout
          const isSelected = selectedSeedType === seed.type
          
          return (
            <button
              key={seed.type}
              onClick={() => setSelectedSeedType(seed.type)}
              className={`relative bg-green-100 border-2 rounded-lg p-3 transition-all ${
                isSelected 
                  ? 'border-green-600 bg-green-200 scale-105' 
                  : 'border-green-300 hover:border-green-400'
              }`}
            >
              {/* Quantity badge */}
              <div className="absolute top-1 right-1 bg-green-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                {seed.quantity}
              </div>
              
              {/* Icon */}
              <Icon size={32} className="mx-auto text-green-600 mb-2" />
              
              {/* Name */}
              <p className="text-center text-sm font-medium text-gray-800">
                {seed.config?.name || t('seedSelector.defaultSeedName')}
              </p>
              
              {/* Color indicator */}
              {seed.config?.color && (
                <div 
                  className="w-full h-2 rounded mt-2"
                  style={{ backgroundColor: seed.config.color }}
                />
              )}
              {seed.config?.colorGradient && (
                <div 
                  className="w-full h-2 rounded mt-2"
                  style={{
                    background: `linear-gradient(to right, ${seed.config.colorGradient.join(', ')})`
                  }}
                />
              )}
            </button>
          )
        })}
      </div>

      {selectedSeedType && (
        <Button
          onClick={handlePlant}
          className="w-full"
        >
          {t('seedSelector.plant')} {config.plants[selectedSeedType]?.name}
        </Button>
      )}
    </div>
  )
}

export default SeedSelector
